import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Fragment, useContext, useEffect, useState } from "react";
import { GreyButton } from '../../../../components/UI/Button';
import { useTranslation } from "react-i18next";
import UserContext from "../../../../store/user-context";
import Checkbox from "../../../UI/Checkbox";
import HtmlInput from "../../../UI/HtmlInput";
import AlertModal from "../../../UI/Modal/AlertModal";
import { ReactComponent as Warning } from '../../../../assets/icons/alerticon_warning.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageDropdown from "../../../UI/LanguageDropdown";
import useFetch from "../../../../hooks/use-fetch";
import { getLangCode } from "../../../Helpers/Util/User";

const Settings = ({ setActiveSection, handleLearningLangUpdate }) => {
    const { t: translate, i18n } = useTranslation();
    const { user, setUser } = useContext(UserContext);
    const { apiUrl } = window['appConfig'];
    const {data, error: updatingError, initFunc: updateProfile} = useFetch();

    const {data: settingsData, error: settingsError, initFunc: fetchUserSettings} = useFetch();
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState(null);
    const fetchOptions = {
        method: 'GET',
    };

    const userFields = {
        firstname: user.user.firstname || '',
        lastname: user.user.lastname || '',
        nickname: user.user.nickname || '',
        learning: user.user.learning || '',
        language: user.user.language || '',
        allCommsOptOut: user.user.allCommsOptOut || false,
        learningReminderCommsOptOut: user.user.learningReminderCommsOptOut,
        learningSummaryCommsOptOut: user.user.learningSummaryCommsOptOut,
        internal: user.user.internal || true,
        newsletterCommsOptOut: user.user.internal || true,
    };

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [buttonState, setButtonState] = useState('ready');
    const [userValues, setUserValues] = useState(userFields);
    const [userErrors, setUserErrors] = useState({});

    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    
    useEffect(() => {
        setIsLoading(true);
        fetchUserSettings({url:`${apiUrl}/user`, options: fetchOptions})
    }, [])

    useEffect(() => {
        if(settingsError) {
            setErrors(settingsError)
        }
    }, [settingsError])

    useEffect(() => {
        if( settingsData && ! settingsError) {
            setUser((prevState) => ({
                ...prevState,
                user: settingsData,
              }));
              setIsLoading(false);
        }
    },[settingsData, settingsError])

    useEffect(() => {
        if( data ) {
            //Update UI
            setIsSubmitting(false);
            setButtonState('ready');

             setUser((prevState) => ({
                ...prevState,
                user: {
                    ...prevState.user,
                    firstname: data.firstname,
                    lastname: data.lastname,
                    nickname: data.nickname,
                    language: data.language,
                    learning: data.learning,
                    learningReminderCommsOptOut: data.learningReminderCommsOptOut,
                    learningSummaryCommsOptOut: data.learningSummaryCommsOptOut,
                },
            }));

            //Reset fields
            setUserValues(userFields);

            if( data.language !== user.user.language ) {
                i18n.changeLanguage(getLangCode(data.language));
            }
            
            //User has changed learning lang, need to go fetch correct data on dashboard
            if( data.learning !== user.user.learning ) {
                handleLearningLangUpdate();
            }
        }
    }, [data])

    useEffect(() => {
        if (updatingError) {
            //Update UI
            setIsSubmitting(false);
            setButtonState('ready');

            let status = updatingError.status;
            const message = translate('user-update-error', { status });
            setAlertMessage(message);
            setShowAlertModal(true);
        } 
    }, [updatingError])

    useEffect(() => {
        if (Object.keys(userErrors).length === 0 && isSubmitting) {
            submitForm();
        }
    }, [userErrors]);

    if( isLoading ) {
        return <div className={`user-dashboard user-dashboard__inner`}>
            <div className="user-dashboard__header">
                <button className="user-dashboard__back" onClick={() => {setActiveSection('')}}><FontAwesomeIcon icon={faChevronLeft} size="1x" /> {translate('back')}</button>
                <h4 className="user-dashboard__title">{translate('settings')}</h4>
                <p>{translate('hof-blurb')}</p>
            </div>
            <p className="user-dashboard__loading"></p>
        </div>
    }

    if( errors ) {
        return (
            <div className={`user-dashboard user-dashboard__inner`}>
                <div className="user-dashboard__header">
                    <button className="user-dashboard__back" onClick={() => {setActiveSection('')}}><FontAwesomeIcon icon={faChevronLeft} size="1x" /> {translate('back')}</button>
                    <h4 className="user-dashboard__title">{translate('learnt-fragments')}</h4>
                    <div className="dashboard-section">
                        <h4 className="dashboard-section__title">{translate('error-has-occured')}</h4>
                        <div className="error-container">
                            <details  className="error-report" style={{ whiteSpace: 'pre-wrap' }}>{errors}</details>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // const [permission, setPermission] = useState(!user.user.allCommsOptOut);

    const handleChange = (e) => {
        let { name, value, type, checked } = e.target;

        if( type === 'checkbox' ) {
            value = checked;
        }

        setUserValues({
            ...userValues,
            [name]: value,
        });
    };

    const handleDropdownChange = (name, value) => {
        setUserValues({
            ...userValues,
            [name]: value,
        });
      };

    const userDetailsButtonText =
        buttonState === 'loading' ? translate('updating-profile') : translate('update-profile');

    const userDetailsFormHandler = (e) => {
        e.preventDefault();
        setUserErrors({});

        //Error Checks
        if (!userValues.firstname) {
            setUserErrors((prevState) => ({
                ...prevState,
                firstname: translate('firstname-required'),
            }));
        }

        if (!userValues.lastname) {
            setUserErrors((prevState) => ({
                ...prevState,
                lastname: translate('surname-required'),
            }));
        }

        if (!userValues.nickname) {
            setUserErrors((prevState) => ({
                ...prevState,
                nickname: translate('nickname-required'),
            }));
        }

        setIsSubmitting(true);
    };

    // const submitForm = () => {
        

    //     const updatingUser = updateUser();
    //     updatingUser.then((response) => {
    //         setIsSubmitting(false);
    //         setButtonState('ready');

    //         if (response !== 200) {
    //             return;
    //         }

    //         setUserValues(userFields);
    //     });
    // };

    const submitForm = async () => {
        setButtonState('loading');
        const formData = new URLSearchParams();

        formData.append('firstname', userValues.firstname);
        formData.append('lastname', userValues.lastname);
        formData.append('nickname', userValues.nickname);
        formData.append('language', userValues.language);
        formData.append('learning', userValues.learning);
        formData.append(
            'learningReminderCommsOptOut',
            userValues.learningReminderCommsOptOut === 'true' ||
            userValues.learningReminderCommsOptOut === true,
        );
        formData.append(
            'learningSummaryCommsOptOut',
            userValues.learningSummaryCommsOptOut === 'true' ||
            userValues.learningSummaryCommsOptOut === true,
        );

        const fetchOptions = {
            method: 'PUT',
            body: formData,
        };
          
        updateProfile({ url: `${apiUrl}/user`, options: fetchOptions });
    };

    return (
        <Fragment>
            <div className={`user-dashboard user-dashboard__inner`}>
                <div className="user-dashboard__header">
                    <button className="user-dashboard__back" onClick={() => { setActiveSection('') }}><FontAwesomeIcon icon={faChevronLeft} size="1x" /> {translate('back')}</button>
                    <h4 className="user-dashboard__title">{translate('settings')}</h4>
                </div>
                <div className="dashboard-section">
                    <h4 className="dashboard-section__title">{translate('user-details')}</h4>
                    <form className="user-details" onSubmit={userDetailsFormHandler}>
                        <div
                            className={`input-row input-row--half ${userErrors.firstname ? 'input-row--error' : ''
                                }`}
                        >
                            <HtmlInput
                                type="text"
                                id="firstname"
                                name="firstname"
                                legend={translate('firstname')}
                                value={userValues.firstname}
                                changeCallback={handleChange}
                                hasErrors={'firstname' in userErrors}
                            />
                            {userErrors.firstname && (
                                <p className="input-row__error">{userErrors.firstname}</p>
                            )}
                        </div>
                        <div
                            className={`input-row input-row--half ${userErrors.lastname ? 'input-row--error' : ''
                                }`}
                        >
                            <HtmlInput
                                type="text"
                                id="surname"
                                name="surname"
                                legend={translate('surname')}
                                value={userValues.lastname}
                                changeCallback={handleChange}
                                hasErrors={'surname' in userErrors}
                            />
                            {userErrors.lastname && (
                                <p className="input-row__error">{userErrors.lastname}</p>
                            )}
                        </div>
                        <div
                            className={`input-row input-row--half ${userErrors.nickname ? 'input-row--error' : ''
                                }`}
                        >
                            <HtmlInput
                                type="text"
                                id="nickname"
                                name="nickname"
                                legend={translate('nickname')}
                                value={userValues.nickname}
                                changeCallback={handleChange}
                                hasErrors={'nickname' in userErrors}
                            />
                            {userErrors.nickname && (
                                <p className="input-row__error">{userErrors.nickname}</p>
                            )}
                        </div>
                        <h4 className="dashboard-section__title">{translate('communications')}</h4>
                        <div
                            className={`input-row input-row--half input-row--thin ${userErrors.learningReminderCommsOptOut ? 'input-row--error' : ''
                                }`}
                        >
                            <Checkbox
                                id="learningReminderCommsOptOut"
                                name="learningReminderCommsOptOut"
                                label={translate('learning-reminders')}
                                checkedByDefault={userValues.learningReminderCommsOptOut}
                                // changeCallback={handlePreferencesChange('learningReminderCommsOptOut',)}
                                changeCallback={handleChange}
                            />
                        </div>
                        <div
                            className={`input-row input-row--half input-row--thin ${userErrors.learningSummaryCommsOptOut ? 'input-row--error' : ''
                                }`}
                        >
                            <Checkbox
                                id="learningSummaryCommsOptOut"
                                name="learningSummaryCommsOptOut"
                                label={translate('weekly-reminders')}
                                checkedByDefault={userValues.learningSummaryCommsOptOut}
                                // changeCallback={handlePreferencesChange('learningSummaryCommsOptOut',)}
                                changeCallback={handleChange}
                            />
                        </div>
                        <h4 className="dashboard-section__title">{translate('language-choices')}</h4>
                        <div className="input-row input-row--half">
                            <h4 className="input-row__title">{translate('choose-language')}</h4>
                            {<LanguageDropdown 
                                defaultValue={userValues.learning} 
                                allowGA={false}
                                name="learning"
                                onClickHandler={handleDropdownChange}
                            />}
                        </div>
                        <div className="input-row input-row--half">
                            <h4 className="input-row__title">{translate('choose-native-language')}</h4>
                            {<LanguageDropdown 
                                defaultValue={userValues.language} 
                                allowGA={false}
                                name="language"
                                onClickHandler={handleDropdownChange}
                                onlyShowNative={true}
                            />}
                        </div>
                        <button
                            className="button button--success mt-30"
                            name="user-update"
                            type="submit"
                            onClick={userDetailsFormHandler}
                            disabled={buttonState === 'loading' ? true : false}
                            >
                            {userDetailsButtonText}
                            </button>
                    </form>
                </div>
                <div className="user-dashboard__section-logout">
                    <button 
                        className='button button--logout'
                        style={{maxWidth: '200px'}}
                        onClick={() => {
                            setUser(null);
                            localStorage.clear('user');
                            // history.push('/');
                        }}
                    >
                        <LogoutIcon />
                        {translate('logout')}
                    </button>
                </div>
            </div>
            <AlertModal
                isOpen={showAlertModal}
                handleClose={() => setShowAlertModal(false)}
                title={'Warning'}
                message={alertMessage}
                closeMessage="Close"
                icon={<Warning style={{ height: '60px' }} />}
                closeButtonType={GreyButton}
            />
        </Fragment>
    )
}

export default Settings